import { AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Check, Headphones, FileText, Target, Sparkles, RotateCcw, Loader2 } from "lucide-react";
import { fmtDoneAt } from "@/components/bootcamp/format";
import ResourceCard, { primaryFlags, segFromLink } from "@/components/vetting/ResourceCard";

const SECTIONS = [
  ["podcasts", "Podcasts", Headphones],
  ["resources", "Resources", FileText],
  ["actions", "Today's action", Target],
  ["extra_resources", "Extra resources", Sparkles],
];

function Item({ item, kind, primary, onOpenResources, testid }) {
  if (/valuable resources/i.test(item.text)) {
    return (
      <button onClick={onOpenResources} data-testid={testid}
              className="min-h-[44px] text-left text-[15px] font-medium text-[#0A1F44] underline decoration-[#C9A961] underline-offset-4 hover:text-[#8A6D1F]">
        {item.text}
      </button>
    );
  }
  if (item.url) return <ResourceCard seg={segFromLink(item.text, item.url, kind === "podcasts" ? "audio" : undefined)} primary={primary} testid={testid} />;
  return <span className="text-[16px] sm:text-[15px] text-slate-800 leading-7" data-testid={testid}>{item.text}</span>;
}

export default function BootcampDay({ day, doneAt, readOnly, busy, onToggle, onOpenResources }) {
  const done = !!doneAt;
  // Key media: the first video/audio and first image in Podcasts + Resources (Sean's top-placed items).
  const keyItems = ["podcasts", "resources"].flatMap((k) => (day[k] || []).map((it, i) => ({ k, i, it }))).filter((x) => x.it.url);
  const keyFlags = primaryFlags(keyItems.map((x) => segFromLink(x.it.text, x.it.url, x.k === "podcasts" ? "audio" : undefined)), true);
  const isKey = (k, i) => keyItems.some((x, j) => keyFlags[j] && x.k === k && x.i === i);
  return (
    <AccordionItem value={`day-${day.day}`} id={`bootcamp-day-${day.day}`} data-testid={`bootcamp-day-${day.day}`}
                   className={`rounded-xl border mb-2 scroll-mt-32 bg-white ${done ? "border-[#C9A961]/60" : "border-slate-200"}`}>
      <AccordionTrigger className="px-3 sm:px-4 py-3 hover:no-underline" data-testid={`bootcamp-day-trigger-${day.day}`}>
        <div className="flex items-center gap-3 text-left min-w-0">
          <span className={`w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-sm font-semibold tabular-nums ${
            done ? "bg-[#C9A961] text-[#0A1F44]" : "bg-[#0A1F44]/5 text-[#0A1F44]"}`}
                data-testid={`bootcamp-day-status-${day.day}`}>
            {done ? <Check className="w-4 h-4" /> : day.day}
          </span>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9A961] font-semibold">
              Day {day.day}{done ? " · Complete" : ""}
            </div>
            <div className="text-[15px] sm:text-sm font-semibold text-slate-900 leading-snug">{day.topic}</div>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-3 sm:px-4 pb-4">
        <div className="space-y-4 pl-0 sm:pl-12">
          {SECTIONS.filter(([k]) => day[k]?.length).map(([k, label, Icon]) => (
            <div key={k} data-testid={`bootcamp-day-${day.day}-${k}`}>
              <div className="flex items-center gap-1.5 text-[12px] uppercase tracking-[0.16em] text-[#0A1F44] font-semibold mb-2">
                <Icon className="w-4 h-4 text-[#8A6D1F]" /> {label}
              </div>
              <ul className="space-y-2">
                {day[k].map((it, i) => (
                  <li key={i}><Item item={it} kind={k} primary={isKey(k, i)} onOpenResources={onOpenResources} testid={`bootcamp-day-${day.day}-${k}-${i}`} /></li>
                ))}
              </ul>
            </div>
          ))}
          <div className="flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-slate-100">
            <span className="text-xs text-slate-500" data-testid={`bootcamp-day-done-at-${day.day}`}>
              {done ? `Completed ${fmtDoneAt(doneAt)}` : "Not completed yet"}
            </span>
            {!readOnly && (
              <Button size="sm" disabled={busy} onClick={() => onToggle(day.day, !done)} data-testid={`bootcamp-day-toggle-${day.day}`}
                      className={`min-h-[44px] px-4 ${done ? "rounded-full bg-white text-[#0A1F44] border border-slate-300 hover:bg-slate-50"
                                      : "rounded-full bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90"}`}>
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : done ? <RotateCcw className="w-4 h-4 mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                {done ? "Reopen day" : "Mark day complete"}
              </Button>
            )}
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
